<script lang="ts">
  /**
   * A filled tile that starts something together: "Play co-op", "Play
   * versus", "Create a room". An icon in a round well, a title, one line of
   * detail. TwinTen's together-tab mode tiles.
   *
   * - `tone`: `accent` (the first) or `second` (`--ui-accent-deep`, the
   *   second of a pair), so two tiles side by side are told apart.
   * - `layout`: `row` (icon left, the two lines beside it) for a tile alone
   *   on its row, `stacked` (icon, title, detail under each other) for two
   *   tiles side by side (the layout around them is the screen's).
   *
   * Test id: `testid`. No default.
   */
  import type { Snippet } from 'svelte';

  interface Props {
    readonly title: string;
    readonly detail?: string | undefined;
    /** The glyph, 24px, in `currentColor` (an `Icon`). */
    readonly icon: Snippet;
    readonly tone?: 'accent' | 'second';
    readonly layout?: 'row' | 'stacked';
    readonly disabled?: boolean;
    readonly onclick: () => void;
    readonly testid?: string | undefined;
  }
  const {
    title,
    detail,
    icon,
    tone = 'accent',
    layout = 'row',
    disabled = false,
    onclick,
    testid,
  }: Props = $props();
</script>

<button
  type="button"
  class="ui-start-tile ui-start-tile--{tone} ui-start-tile--{layout}"
  {disabled}
  data-testid={testid}
  {onclick}
>
  <span class="ui-start-tile-icon" aria-hidden="true">{@render icon()}</span>
  <span class="ui-start-tile-title">{title}</span>
  {#if detail !== undefined}
    <span class="ui-start-tile-detail">{detail}</span>
  {/if}
</button>

<style>
  .ui-start-tile {
    box-sizing: border-box;
    width: 100%;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    column-gap: 0.75rem;
    align-items: center;
    min-height: 4.5rem;
    padding: 0.85rem 1rem;
    border: 0;
    border-radius: var(--ui-radius-lg);
    background: var(--ui-accent);
    color: var(--ui-on-accent);
    font-family: var(--ui-font);
    text-align: left;
  }

  .ui-start-tile--second {
    background: var(--ui-accent-deep);
  }

  .ui-start-tile:disabled {
    opacity: 0.55;
  }

  .ui-start-tile:focus-visible {
    outline: 3px solid var(--ui-ink);
    outline-offset: 3px;
  }

  .ui-start-tile-icon {
    grid-row: 1 / span 2;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: color-mix(in srgb, var(--ui-on-accent) 20%, transparent);
  }

  .ui-start-tile-title {
    font: var(--ui-text-heading);
  }

  .ui-start-tile-detail {
    font: var(--ui-text-label);
  }

  .ui-start-tile--stacked {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
    row-gap: 0.3rem;
  }

  .ui-start-tile--stacked .ui-start-tile-icon {
    grid-row: auto;
  }
</style>
